/**
 * iter87.361 — NWSL Match Predictor breakdown, sibling of
 * `NbaSeriesBreakdownModal.jsx` but 3 sub-results per row instead of
 * 1. Only ever opened for a round once every fixture in it is final
 * (gated by the caller via `nwsl_predictor_by_round`/
 * `nwsl_rounds_all_played`, same reveal rule as the spread/Series
 * Predictor breakdown modals).
 */
import { X, Check, Minus, Target } from "lucide-react";

export default function NwslPredictorBreakdownModal({ open, onClose, playerName, rounds = [], teamsById = {}, totalPoints = 0 }) {
    if (!open) return null;
    const nameFor = (id) => teamsById[id]?.name || (id ? id : "TBD");
    const roundsWithFixtures = rounds.filter((r) => (r.fixtures || []).length > 0);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="nwsl-predictor-breakdown-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-md w-full max-h-[85dvh] flex flex-col"
                data-testid="nwsl-predictor-breakdown-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <Target className="w-5 h-5 text-cyber shrink-0" />
                        <div className="min-w-0">
                            <h2 className="font-display font-black text-lg uppercase truncate">Playoff Predictor Picks</h2>
                            <p className="text-[11px] text-zinc-500 truncate">{playerName}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="nwsl-predictor-breakdown-modal-close-btn"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-3 flex-1 space-y-4" data-testid="nwsl-predictor-breakdown-list">
                    {roundsWithFixtures.length === 0 && (
                        <p className="py-6 text-center text-xs text-zinc-500" data-testid="nwsl-predictor-breakdown-empty">
                            No completed rounds yet — picks reveal once a full round is final.
                        </p>
                    )}
                    {roundsWithFixtures.map((r) => (
                        <div key={r.key} data-testid={`nwsl-predictor-breakdown-round-${r.key}`}>
                            <p className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">{r.label}</p>
                            <div className="space-y-1.5">
                                {(r.fixtures || []).map((fx) => (
                                    <div
                                        key={fx.id}
                                        data-testid={`nwsl-predictor-breakdown-row-${fx.id}`}
                                        className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5"
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <p className="text-xs text-zinc-300 truncate">
                                                {nameFor(fx.team_a_id)} <span className="text-zinc-600">vs</span> {nameFor(fx.team_b_id)}
                                            </p>
                                            {fx.played && (
                                                <span className={`flex items-center gap-1 shrink-0 font-mono font-bold text-sm ${fx.points > 0 ? "text-neon" : "text-zinc-500"}`}>
                                                    +{fx.points}
                                                </span>
                                            )}
                                        </div>
                                        {!fx.pick ? (
                                            <p className="text-[10px] text-zinc-600">No pick</p>
                                        ) : (
                                            <div className="flex flex-wrap gap-2 text-[10px]">
                                                <span className={`flex items-center gap-1 ${fx.winner_correct ? "text-neon" : "text-zinc-500"}`}>
                                                    {fx.winner_correct ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />} Winner
                                                </span>
                                                <span className={`flex items-center gap-1 ${fx.time_correct ? "text-neon" : "text-zinc-500"}`}>
                                                    {fx.time_correct ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />} Decided-by
                                                </span>
                                                <span className={`flex items-center gap-1 ${fx.goals_correct ? "text-neon" : "text-zinc-500"}`}>
                                                    {fx.goals_correct ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />} Goals O/U
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center justify-between px-5 py-3 border-t-2 border-[#222]">
                    <span className="font-black uppercase tracking-widest text-[10px] text-zinc-400">Total</span>
                    <span className="font-mono font-black text-base text-white" data-testid="nwsl-predictor-breakdown-total">
                        {totalPoints > 0 ? `+${totalPoints}` : totalPoints}
                    </span>
                </div>
            </div>
        </div>
    );
}
